import { yupResolver } from "@hookform/resolvers/yup";
import type { FlagProps } from "@wayflyer/flyui";
import { StatusBanner, Flag, Flex, Link, Text } from "@wayflyer/flyui";
import { RhfAutocompleteField } from "@wayflyer/flyui-hook-form";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import type { BankConnectCountryConfig } from "shared-ui";
import * as Yup from "yup";

import { useBankConnectContext } from "../context/use-bank-connect-context";
import { MESSAGES } from "../message-definitions";

export interface CountrySelectionFormProps {
  initialCountry?: BankConnectCountryConfig;
  onConfirm: ((data: any) => Promise<void>) | ((data: any) => void);
}

/**
 * CountrySelectionModal component
 */
export const CountrySelectionForm = ({ onConfirm, initialCountry }: CountrySelectionFormProps) => {
  const intl = useIntl();
  const bankConnectProps = useBankConnectContext();
  const handleConfirm = async (formData: { country: string }) => {
    const selection = bankConnectProps?.countryOptions?.find((value) => value.code === formData.country);
    await onConfirm(selection);
  };

  const countryOptions = useMemo(
    () =>
      bankConnectProps?.countryOptions?.map((item) => ({
        ...item,
        text: item.name,
        id: item.code,
        icon: <Flag code={item.code as FlagProps["code"]} />,
      })),
    [bankConnectProps?.countryOptions]
  );
  const schema = Yup.object().shape({
    country: Yup.string().trim().required(intl.formatMessage(MESSAGES.selectCountryError)),
  });
  const countrySelectForm = useForm<{ country: string }>({ resolver: yupResolver(schema) });

  return (
    <Flex direction="column" gap="4">
      <form onSubmit={countrySelectForm.handleSubmit(handleConfirm)}>
        <RhfAutocompleteField
          name="country"
          initialValue={initialCountry?.code}
          options={countryOptions}
          form={countrySelectForm}
          label={intl.formatMessage({
            id: "bankconnect: select bank location label",
            defaultMessage: "Select bank location",
          })}
          inlineButton={{
            label: intl.formatMessage({
              id: "bankconnect: select bank location submit label",
              defaultMessage: "Select Bank",
            }),
            type: "submit",
          }}
        />
      </form>
      {/* eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed. */}
      {bankConnectProps.basiqConnectProps.openBankingEnabled && countrySelectForm.watch("country") === "AU" && (
        <StatusBanner
          variant="info"
          title="We use Basiq to connect with Open Banking"
          subheading={
            <Flex direction="column" gap="0">
              <Text>
                {intl.formatMessage(
                  {
                    id: "bankconnect: opt in to connect open banking",
                    defaultMessage: "You may need to <b>opt-in to data sharing</b> with your bank before connecting.",
                    description:
                      "Pre-connection message for Australian Open Banking, informing of the requirement to enable data sharing on the bank side",
                  },
                  {
                    b: (chunks: string) => (
                      <Text element="span" fontWeight="semiBold">
                        {chunks}
                      </Text>
                    ),
                  }
                )}
              </Text>
              <Text>
                {intl.formatMessage(
                  {
                    id: "bankconnect: data sharing help link",
                    defaultMessage: "See our <a>Data Sharing Help</a> page for details.",
                    description: "Link to our Data Sharing Help page",
                  },
                  {
                    a: (chunks: string) => (
                      <Link
                        url="https://help-center.wayflyer.com/en/articles/361036-how-do-i-opt-in-to-data-sharing-in-australia"
                        external
                      >
                        {chunks}
                      </Link>
                    ),
                  }
                )}
              </Text>
            </Flex>
          }
        />
      )}
      <Text color="placeholder">
        {intl.formatMessage(
          {
            id: "bankconnect: proceeding means consent",
            defaultMessage:
              "By proceeding, you consent to Wayflyer processing your bank data in line with our <a>privacy notice.</a>",
            description:
              "Shown at start of connecting bank accounts to inform user that proceeding counts as consent for data to be processed",
          },
          {
            a: (chunks: string) => (
              <Link url="https://www.wayflyer.com/privacy-notice#terms-of-business">{chunks}</Link>
            ),
          }
        )}
      </Text>
    </Flex>
  );
};
